import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  type ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type PaginationState,
  type Row,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useRef, useState } from 'react';
import { listFilteredProjects as apiListFilteredProjects } from '~/api/civilsearch';
import type { ProjectWithDataRequests } from '~/common/types';
import { SearchAsync, type SearchAsyncColumnDef, type SearchRef } from '~/components2/Table';
import AdvancedFilterV2, {
  type AdvancedFilterColumnDef,
  reactTableToSearchParams,
} from '~/components2/Table/AdvancedFilterV2';
import { useIsKeyDown } from '~/hooks/useIsKeyDown';
import CivilSearchLayout from '../common/CivilSearchLayout';
import CivilSearchTable from '../common/CivilSearchTable';
import { FetchErrorDisplay } from '../common/FetchErrorDisplay';
import { columns } from './columns';
import { NOT_INTERNAL_ORG } from './filterShortcuts';

const filterColumns: AdvancedFilterColumnDef[] = [
  { id: 'project.name', type: 'text' },
  { id: 'organization.name', type: 'text' },
  { id: 'organization.isInternal', type: 'boolean' },
  { id: 'project.projectId', type: 'text' },
];

const searchColumns: SearchAsyncColumnDef[] = [
  { id: 'project.name', type: 'text' },
  { id: 'organization.name', type: 'text' },
  { id: 'project.projectId', type: 'text' },
];

const fallbackData: ProjectWithDataRequests[] = [];

export default function Projects() {
  const navigate = useNavigate();
  const searchRef = useRef<SearchRef>(null);
  const [isMetaKeyDown] = useIsKeyDown('Meta');
  const [totalRows, setTotalRows] = useState<number>(0);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: 'project.createdAt', desc: true }]);
  const [filterParams, setFilterParams] = useState<{ [key: string]: any } | undefined>(undefined);

  const query = useQuery({
    enabled: !!filterParams,
    gcTime: 0,
    refetchOnWindowFocus: false,
    queryKey: ['civilsearch-projects', { pagination, sorting, filters: filterParams }],
    queryFn: async () => {
      const res = await apiListFilteredProjects({
        search: reactTableToSearchParams({
          pagination,
          sorting,
          filters: filterParams,
        }),
      });

      setTotalRows(res.totalDocuments || 0);
      return {
        ...res,
        projects: res.projects as ProjectWithDataRequests[],
      };
    },
  });

  const { pageIndex, pageSize } = pagination;
  const isError = query.isError;
  const isLoading = query.isFetching;
  const lastIndex = Math.floor(totalRows / pageSize);

  const table = useReactTable<ProjectWithDataRequests>({
    data: query.data?.projects ?? fallbackData,
    columns: columns as ColumnDef<ProjectWithDataRequests>[],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableSorting: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    autoResetPageIndex: false,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getRowId: (r) => r.id || r.id,
    state: {
      pagination,
      sorting,
      columnVisibility: { refId: false },
    },
  });

  const onRowClick = (row: Row<ProjectWithDataRequests>) => {
    const id = row.original.id;
    const location = `/admin/civil-search/projects/${id}`;

    if (isMetaKeyDown) {
      const url = `${window.location.origin}${location}`;
      window.open(url, '_blank');
    } else {
      navigate({ to: location });
    }
  };

  if (isError) {
    return <FetchErrorDisplay label="projects" error={query.error} onRetry={() => query.refetch()} />;
  }

  return (
    <CivilSearchLayout title="Projects with Data Requests">
      <div className="h-full text-xs">
        <CivilSearchTable
          id="projects"
          table={table}
          isLoading={isLoading}
          onRowClick={onRowClick}
          onCellClick={(cell) => {
            onRowClick(cell.row);
          }}
          pagination={{
            rowCount: totalRows,
            nextPage: {
              disabled: pageIndex === lastIndex,
              onClick: () => table.nextPage(),
            },
            lastPage: {
              disabled: pageIndex === lastIndex,
              onClick: () => {
                setPagination((prev) => ({ ...prev, pageIndex: lastIndex }));
              },
            },
          }}
        >
          <AdvancedFilterV2
            id="civilsearch-projects"
            table={table}
            columns={filterColumns}
            onQuery={(_, params) => {
              searchRef.current?.clear();
              setFilterParams(params);
            }}
            defaultFilterState={[NOT_INTERNAL_ORG]}
            shortcuts={[
              {
                label: 'Exclude Internal Org',
                filterState: [NOT_INTERNAL_ORG],
              },
            ]}
          />

          <SearchAsync
            table={table}
            searchRef={searchRef}
            disabled={isLoading}
            columns={searchColumns}
            onCommit={(_, params) => {
              setFilterParams(params);
            }}
          />
        </CivilSearchTable>
      </div>
    </CivilSearchLayout>
  );
}
